import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import apollo from "@/helpers/apollo";
import {
  type DataType,
  QUERY,
  type VariablesType,
} from "@/pages/policies/graphql";

interface PoliciesInput {
  company: string;
  location: string;
}

interface PolicyItem {
  policy: string;
  title: string;
}

interface PoliciesResult {
  cancellationPolicy: PolicyItem;
  otherReservationPolicies: PolicyItem;
  reschedulingPolicy: PolicyItem;
}

async function fetchPoliciesData(
  params: PoliciesInput
): Promise<PoliciesResult | null> {
  const client = apollo();
  const variables: VariablesType = {
    companySlug: params.company,
    locationSlug: params.location,
  };
  const response = await client.query<DataType, VariablesType>({
    fetchPolicy: "no-cache",
    query: QUERY,
    variables,
  });

  if (!response.data.policies) {
    return null;
  }

  return {
    cancellationPolicy: {
      policy: response.data.policies.cancellationPolicy || "",
      title: "Cancellation policy",
    },
    otherReservationPolicies: {
      policy: response.data.policies.otherReservationPolicies || "",
      title: "Booking policy",
    },
    reschedulingPolicy: {
      policy: response.data.policies.reschedulingPolicy || "",
      title: "Rescheduling policy",
    },
  };
}

export const fetchPoliciesIndex = createServerFn({ method: "GET" })
  .inputValidator((data: PoliciesInput) => data)
  .handler(async ({ data }) => {
    const policies = await fetchPoliciesData(data);
    if (!policies) {
      throw notFound();
    }

    if (
      !(
        policies.cancellationPolicy.policy || policies.reschedulingPolicy.policy
      )
    ) {
      if (!policies.otherReservationPolicies.policy) {
        throw notFound();
      }
      policies.otherReservationPolicies.title = "General";
    }

    return { policies: Object.values(policies), title: "Policies" };
  });

export const fetchPoliciesCancellation = createServerFn({ method: "GET" })
  .inputValidator((data: PoliciesInput) => data)
  .handler(async ({ data }) => {
    const policies = await fetchPoliciesData(data);
    if (!policies?.cancellationPolicy.policy) {
      throw notFound();
    }
    return {
      policies: [policies.cancellationPolicy],
      title: policies.cancellationPolicy.title,
    };
  });

export const fetchPoliciesRescheduling = createServerFn({ method: "GET" })
  .inputValidator((data: PoliciesInput) => data)
  .handler(async ({ data }) => {
    const policies = await fetchPoliciesData(data);
    if (!policies?.reschedulingPolicy.policy) {
      throw notFound();
    }
    return {
      policies: [policies.reschedulingPolicy],
      title: policies.reschedulingPolicy.title,
    };
  });

export const fetchPoliciesOther = createServerFn({ method: "GET" })
  .inputValidator((data: PoliciesInput) => data)
  .handler(async ({ data }) => {
    const policies = await fetchPoliciesData(data);
    if (!policies?.otherReservationPolicies.policy) {
      throw notFound();
    }
    return {
      policies: [policies.otherReservationPolicies],
      title: policies.otherReservationPolicies.title,
    };
  });
